import { useEffect, useRef } from 'react';

/**
 * Hook für Scroll-basierte Einblend-Animationen via IntersectionObserver.
 * Fügt die CSS-Klasse `is-visible` hinzu, sobald das Element im Viewport sichtbar ist.
 * Erfordert `.reveal-block`-CSS-Klasse auf dem Element.
 *
 * @param threshold - Sichtbarkeits-Schwellwert (0–1). Standard: 0.12
 * @returns React ref — an das zu animierende Element hängen
 *
 * @example
 * ```tsx
 * const ref = useScrollReveal();
 * return <div ref={ref} className="reveal-block">...</div>;
 * ```
 */
export function useScrollReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          el.classList.add('is-visible');
          observer.unobserve(el);
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return ref;
}
